import { useState, useEffect, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, LabelList } from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import MmEmailRecipientsCard from "@/components/mm/MmEmailRecipientsCard";

interface MonthRow {
  date: string;
  iso: string;
  returns: (number | null)[];
}

interface SheetData {
  funds: string[];
  months: MonthRow[];
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmt = (n: number | null) => (n === null || isNaN(n) ? '--' : n.toFixed(2));

const MONTH_COUNT_OPTIONS = [3, 6, 9, 12, 18, 24, 36];

const CBAMMMReporting = () => {
  const [data, setData] = useState<SheetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIso, setSelectedIso] = useState<string>('');
  const [monthCount, setMonthCount] = useState<number>(3);
  const chartsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: resp, error: invErr } = await supabase.functions.invoke('mm-performance-data');
        if (invErr) throw invErr;
        if (resp?.error) throw new Error(resp.error);
        const sheet = resp as SheetData;
        setData(sheet);
        if (sheet.months.length > 0) {
          setSelectedIso(sheet.months[sheet.months.length - 1].iso);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const months3 = useMemo(() => {
    if (!data || !selectedIso) return [];
    const idx = data.months.findIndex(m => m.iso === selectedIso);
    if (idx === -1) return [];
    const start = Math.max(0, idx - (monthCount - 1));
    return data.months.slice(start, idx + 1);
  }, [data, selectedIso, monthCount]);


  // Build per-month tables (oldest -> newest in chronological order; display newest first like PDF)
  const monthTables = useMemo(() => {
    if (!data) return [];
    const tables = months3.map(m => {
      const monthLabel = MONTH_LABELS[parseInt(m.iso.slice(5, 7), 10) - 1];
      const rows = data.funds.map((fund, i) => {
        const r = m.returns[i];
        const simple = r === null ? null : r * 12;
        const compound = r === null ? null : (Math.pow(1 + r / 100, 12) - 1) * 100;
        return { fund, monthly: r, simple, compound };
      });
      return { monthLabel, iso: m.iso, rows };
    });
    return tables.slice().reverse(); // newest first
  }, [months3, data]);

  // Annualised matrices: rows = funds, cols = months (chronological)
  const annualisedSimple = useMemo(() => {
    if (!data) return null;
    return data.funds.map((fund, i) => ({
      fund,
      values: months3.map(m => (m.returns[i] === null ? null : (m.returns[i] as number) * 12)),
    }));
  }, [data, months3]);

  const annualisedCompound = useMemo(() => {
    if (!data) return null;
    return data.funds.map((fund, i) => ({
      fund,
      values: months3.map(m =>
        m.returns[i] === null ? null : (Math.pow(1 + (m.returns[i] as number) / 100, 12) - 1) * 100
      ),
    }));
  }, [data, months3]);

  // How many months of history exist up to (and including) the selected month
  const availableMonths = useMemo(() => {
    if (!data || !selectedIso) return 0;
    const idx = data.months.findIndex(m => m.iso === selectedIso);
    return idx === -1 ? 0 : idx + 1;
  }, [data, selectedIso]);

  const monthCountOptions = useMemo(() => {
    const opts = MONTH_COUNT_OPTIONS.filter(n => n <= availableMonths);
    if (availableMonths > 0 && !opts.includes(availableMonths)) opts.push(availableMonths);
    return opts.sort((a, b) => a - b);
  }, [availableMonths]);

  // Clamp selection when fewer months are available than currently selected
  useEffect(() => {
    if (availableMonths > 0 && monthCount > availableMonths) {
      setMonthCount(monthCountOptions[monthCountOptions.length - 1] ?? availableMonths);
    }
  }, [availableMonths, monthCount, monthCountOptions]);

  const spansYears = new Set(months3.map(m => m.iso.slice(0, 4))).size > 1;
  const monthHeaders = months3.map(m => {
    const label = MONTH_LABELS[parseInt(m.iso.slice(5, 7), 10) - 1];
    return spansYears ? `${label} ${m.iso.slice(2, 4)}` : label;
  });


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <p className="text-destructive">Failed to load data: {error}</p>
      </div>
    );
  }

  const selectedLabel = (() => {
    if (!selectedIso) return '';
    const m = MONTH_LABELS[parseInt(selectedIso.slice(5, 7), 10) - 1];
    return `${m} ${selectedIso.slice(0, 4)}`;
  })();

  const handleExportPdf = async () => {
    if (!data) return;
    // Company green palette
    const GREEN_DARK: [number, number, number] = [0, 71, 66];   // #004742
    const GREEN: [number, number, number] = [61, 140, 124];     // #3D8C7C
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();

    // Header bar
    doc.setFillColor(...GREEN_DARK);
    doc.rect(0, 0, pageW, 80, 'F');

    // Logo (white version) on the left of the banner
    try {
      const res = await fetch('/images/cbam-logo-updated.png');
      const blob = await res.blob();
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      // Preserve aspect ratio (source ~1920x985)
      const logoH = 44;
      const logoW = logoH * (1920 / 985);
      doc.addImage(dataUrl, 'PNG', 40, (80 - logoH) / 2, logoW, logoH);
    } catch {
      // ignore — header text still renders
    }

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('CBAM MM Reporting', pageW - 40, 36, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Money Market Returns — ${selectedLabel}`, pageW - 40, 56, { align: 'right' });

    let cursorY = 110;
    doc.setTextColor(...GREEN_DARK);

    monthTables.forEach(t => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(t.monthLabel, 40, cursorY);
      cursorY += 6;
      autoTable(doc, {
        startY: cursorY,
        head: [['Fund name', t.monthLabel, 'Simple', 'Compound']],
        body: t.rows.map(r => [r.fund, fmt(r.monthly), fmt(r.simple), fmt(r.compound)]),
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: GREEN_DARK, textColor: 255, halign: 'right' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        theme: 'striped',
        alternateRowStyles: { fillColor: [235, 245, 243] },
        margin: { left: 40, right: 40 },
      });
      cursorY = (doc as any).lastAutoTable.finalY + 18;
    });

    const renderMatrix = (title: string, rows: { fund: string; values: (number | null)[] }[] | null) => {
      if (!rows) return;
      if (cursorY > 700) { doc.addPage(); cursorY = 60; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...GREEN_DARK);
      doc.text(title, 40, cursorY);
      cursorY += 6;
      autoTable(doc, {
        startY: cursorY,
        head: [['Fund Name', ...monthHeaders]],
        body: rows.map(r => [r.fund, ...r.values.map(v => fmt(v))]),
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: GREEN_DARK, textColor: 255, halign: 'right' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
        theme: 'striped',
        alternateRowStyles: { fillColor: [235, 245, 243] },
        margin: { left: 40, right: 40 },
      });
      cursorY = (doc as any).lastAutoTable.finalY + 18;
    };

    renderMatrix('Annualised Simple Return', annualisedSimple);
    renderMatrix('Annualised Compound Return', annualisedCompound);

    // Append charts (each card captured individually for clean pagination)
    if (chartsRef.current) {
      const cards = Array.from(chartsRef.current.querySelectorAll<HTMLElement>(':scope > *'));
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 40;
      const maxW = pageW - margin * 2;
      for (const card of cards) {
        try {
          const canvas = await html2canvas(card, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
          const imgW = maxW;
          const imgH = (canvas.height * imgW) / canvas.width;
          if (cursorY + imgH > pageH - 40) {
            doc.addPage();
            cursorY = 60;
          }
          doc.addImage(canvas.toDataURL('image/png'), 'PNG', margin, cursorY, imgW, imgH);
          cursorY += imgH + 18;
        } catch {
          // skip on failure
        }
      }
    }

    // Footer on each page
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Cape Berkshire Asset Management  •  FCA 551744  •  Generated ${new Date().toLocaleDateString('en-GB')}`,
        40,
        doc.internal.pageSize.getHeight() - 20
      );
      doc.text(`Page ${i} / ${pageCount}`, pageW - 40, doc.internal.pageSize.getHeight() - 20, { align: 'right' });
    }

    doc.save(`CBAM_MM_Reporting_${selectedIso}.pdf`);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">CBAM MM Reporting</h1>
          <p className="text-muted-foreground mt-2">Money Market Returns</p>
        </div>
        <Button onClick={handleExportPdf} disabled={!data}>
          <Download className="h-4 w-4" />
          Export PDF
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <Label htmlFor="month-select">Month / Year</Label>
            <Select value={selectedIso} onValueChange={setSelectedIso}>
              <SelectTrigger id="month-select" className="w-64">
                <SelectValue placeholder="Select a month" />
              </SelectTrigger>
              <SelectContent>
                {data?.months.slice().reverse().map(m => {
                  const month = MONTH_LABELS[parseInt(m.iso.slice(5, 7), 10) - 1];
                  const year = m.iso.slice(0, 4);
                  return (
                    <SelectItem key={m.iso} value={m.iso}>
                      {month} {year}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Label htmlFor="months-count-select">Months compared</Label>
            <Select value={String(monthCount)} onValueChange={(v) => setMonthCount(Number(v))}>
              <SelectTrigger id="months-count-select" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthCountOptions.map(n => (
                  <SelectItem key={n} value={String(n)}>{n} months</SelectItem>
                ))}

              </SelectContent>
            </Select>
          </div>

        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Money Market Returns</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {monthTables.map(t => (
            <div key={t.iso}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fund name</TableHead>
                    <TableHead className="text-right">{t.monthLabel}</TableHead>
                    <TableHead className="text-right">Simple</TableHead>
                    <TableHead className="text-right">Compound</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {t.rows.map(r => (
                    <TableRow key={r.fund}>
                      <TableCell className="font-medium">{r.fund}</TableCell>
                      <TableCell className="text-right">{fmt(r.monthly)}</TableCell>
                      <TableCell className="text-right">{fmt(r.simple)}</TableCell>
                      <TableCell className="text-right">{fmt(r.compound)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Annualised Simple Return</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fund Name</TableHead>
                {monthHeaders.map(h => (
                  <TableHead key={h} className="text-right">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {annualisedSimple?.map(r => (
                <TableRow key={r.fund}>
                  <TableCell className="font-medium">{r.fund}</TableCell>
                  {r.values.map((v, i) => (
                    <TableCell key={i} className="text-right">{fmt(v)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Annualised Compound Return</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fund Name</TableHead>
                {monthHeaders.map(h => (
                  <TableHead key={h} className="text-right">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {annualisedCompound?.map(r => (
                <TableRow key={r.fund}>
                  <TableCell className="font-medium">{r.fund}</TableCell>
                  {r.values.map((v, i) => (
                    <TableCell key={i} className="text-right">{fmt(v)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data && (() => {
        const palette = [
          '#004742',
          '#004742',
          '#004742',
          '#004742',
          '#004742',
          '#004742',
          '#004742',
          '#004742',
          '#004742',
          '#004742',
        ];
        const slug = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '_');
        const chartFunds = data.funds.filter(f => /cbam\s*mm/i.test(f));
        const chartConfig = chartFunds.reduce((acc, f, i) => {
          acc[slug(f)] = { label: f, color: palette[i % palette.length] };
          return acc;
        }, {} as Record<string, { label: string; color: string }>);

        const buildSeries = (kind: 'monthly' | 'simple' | 'compound') =>
          months3.map(m => {
            const row: Record<string, number | string | null> = {
              month: MONTH_LABELS[parseInt(m.iso.slice(5, 7), 10) - 1],
            };
            chartFunds.forEach(f => {
              const i = data.funds.indexOf(f);
              const r = m.returns[i];
              row[slug(f)] = r === null
                ? null
                : kind === 'monthly'
                  ? r
                  : kind === 'simple'
                    ? r * 12
                    : (Math.pow(1 + r / 100, 12) - 1) * 100;
            });
            return row;
          });

        const renderChart = (title: string, series: ReturnType<typeof buildSeries>) => (
          <Card>
            <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[360px] w-full">
                <LineChart data={series} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => `${Number(v).toFixed(2)}%`} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  {chartFunds.map(f => (
                    <Line
                      key={f}
                      type="monotone"
                      dataKey={slug(f)}
                      name={f}
                      stroke={`var(--color-${slug(f)})`}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls
                    >
                      <LabelList
                        dataKey={slug(f)}
                        position="top"
                        offset={8}
                        formatter={(v: number | null) =>
                          v === null || v === undefined || isNaN(v as number) ? '' : `${Number(v).toFixed(2)}%`
                        }
                        style={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                      />
                    </Line>
                  ))}
                </LineChart>
              </ChartContainer>
            </CardContent>
          </Card>
        );

        return (
          <div ref={chartsRef} className="space-y-6 bg-background">
            {renderChart('Monthly Return (%)', buildSeries('monthly'))}
            {renderChart('Annualised Simple Return (%)', buildSeries('simple'))}
            {renderChart('Annualised Compound Return (%)', buildSeries('compound'))}
          </div>
        );
      })()}

      {/* Admin control: who the monthly Money Market Monitor email goes to. Renders only for
          readers RLS admits (investment team and admins); everyone else sees nothing here. */}
      <MmEmailRecipientsCard />
    </div>
  );
};

export default CBAMMMReporting;
